import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  RefreshCcw,
  ShieldAlert,
  TrendingUp,
  TriangleAlert,
} from "lucide-react";
import PageContainer from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { useLocalization } from "@/lib/localization";
import { useAuth } from "@/lib/store";
import { fetchAnalyticsDemandForecast } from "@/lib/shopApi";

function normalizeDemandForecast(response) {
  return response?.demandForecast ?? null;
}

function formatGeneratedAt(value, locale, copy) {
  if (!value) return copy.liveDemand;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return copy.liveDemand;
  return copy.updatedAt(new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(date));
}

function formatNumber(value, locale) {
  if (value === null || value === undefined) return "0";
  return Number(value).toLocaleString(locale, {
    maximumFractionDigits: 2,
  });
}

export default function AnalyticsDemandForecast() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { locale, messages } = useLocalization();
  const copy = messages.analytics.demandForecast;
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const loadForecast = useCallback(async () => {
    if (!session?.sessionId) {
      setError(messages.analytics.sessionNotReady);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await fetchAnalyticsDemandForecast(session.sessionId);
      setData(normalizeDemandForecast(response));
    } catch (err) {
      setError(err?.message || copy.errorTitle);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [copy.errorTitle, messages.analytics.sessionNotReady, session?.sessionId]);

  useEffect(() => {
    void loadForecast();
  }, [loadForecast]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const forecasts = useMemo(() => data?.forecasts ?? [], [data?.forecasts]);
  const careCount = (data?.watchCount ?? 0) + (data?.irregularCount ?? 0);
  const summaryTone = (data?.stableCount ?? 0) >= 2 && forecasts.some((item) => item.demandBand === "stable" && item.dataConfidence !== "low")
    ? copy.summaryReliable
    : forecasts.length
      ? copy.summaryEarly
      : copy.summaryBuilding;

  return (
    <PageContainer className="px-4 py-6 sm:px-6 lg:px-8">
      <section className="mx-auto w-full max-w-3xl">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="-ml-2 mb-3 h-8 px-2 text-muted-foreground hover:text-foreground"
              onClick={() => navigate("/analytics")}
            >
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              {messages.analytics.analyticsBack}
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">{copy.title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {copy.subtitle}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-9"
            disabled={loading}
            onClick={loadForecast}
          >
            <RefreshCcw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            {messages.analytics.refresh}
          </Button>
        </div>

        {loading ? (
          <div className="luxe-card rounded-lg p-6">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              {copy.loading}
            </div>
          </div>
        ) : null}

        {!loading && error ? (
          <div className="luxe-card rounded-lg border-destructive/25 p-6">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-destructive/10 text-destructive">
                <TriangleAlert className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base font-semibold tracking-tight">{copy.errorTitle}</h2>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">{error}</p>
                <Button className="mt-4 h-9" onClick={loadForecast}>
                  {messages.analytics.tryAgain}
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {!loading && !error && data ? (
          <div className="space-y-4">
            <div className="luxe-card rounded-lg p-5 sm:p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-primary/15">
                    <TrendingUp className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="inline-flex items-center rounded-full border border-border/70 bg-background/70 px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                      {summaryTone}
                    </div>
                    <h2 className="mt-1 text-xl font-semibold tracking-tight">
                      {copy.summaryCounts(data.stableCount ?? 0, careCount)}
                    </h2>
                    <p className="mt-1 max-w-xl text-sm leading-5 text-muted-foreground">
                      {forecasts.length
                        ? copy.withDataDescription
                        : copy.withoutDataDescription}
                    </p>
                  </div>
                </div>
                <div className="pt-1 text-sm text-muted-foreground sm:text-right">
                  {formatGeneratedAt(data.generatedAt, locale, copy)}
                </div>
              </div>

              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                <MiniStat
                  icon={<CheckCircle2 className="h-4 w-4" />}
                  label={copy.steady}
                  value={formatNumber(data.stableCount, locale)}
                />
                <MiniStat
                  icon={<ShieldAlert className="h-4 w-4" />}
                  label={copy.watch}
                  value={formatNumber(data.watchCount, locale)}
                />
                <MiniStat
                  icon={<Activity className="h-4 w-4" />}
                  label={copy.irregular}
                  value={formatNumber(data.irregularCount, locale)}
                />
              </div>
            </div>

            <div className="relative py-1">
              <div className="absolute bottom-0 left-[22px] top-0 hidden w-px bg-border sm:block" />
              {copy.steps.map((step, index) => (
                <FlowStep key={step} index={index + 1} text={step} />
              ))}
            </div>

            <div className="luxe-card rounded-lg px-5 py-5 sm:px-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    {copy.detailedView}
                  </p>
                  <h2 className="mt-1 text-base font-semibold tracking-tight">
                    {forecasts.length ? copy.openFullList : copy.reviewList}
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    {forecasts.length
                      ? copy.fullListDescription(forecasts.length)
                      : copy.reviewListDescription}
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="h-10 rounded-md border-border/70 bg-background/90 px-4 text-[13px] font-medium text-foreground shadow-none transition-colors hover:border-border hover:bg-accent/30 hover:text-foreground"
                  onClick={() => navigate("/analytics/demand-forecast/list")}
                >
                  {copy.viewDemandList}
                  <ArrowRight className="ml-1 h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </section>
    </PageContainer>
  );
}

function MiniStat({ icon, label, value }) {
  return (
    <div className="rounded-md border border-border/70 bg-background/60 px-3 py-3">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-2 font-mono text-sm font-semibold tabular-nums text-foreground">
        {value}
      </div>
    </div>
  );
}

function FlowStep({ index, text }) {
  return (
    <div className="relative flex gap-4 py-2 sm:pl-0">
      <div className="z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-border/70 bg-card font-mono text-sm font-semibold text-primary shadow-sm">
        {index}
      </div>
      <div className="luxe-card flex min-h-11 flex-1 items-center rounded-lg px-4 py-3">
        <p className="text-sm leading-5 text-muted-foreground">{text}</p>
      </div>
    </div>
  );
}

function Metric({ label, value, strong = false }) {
  return (
    <div className="rounded-md border border-border/70 bg-background/60 px-3 py-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </div>
      <div className={`mt-1 font-mono text-sm tabular-nums ${strong ? "font-semibold text-foreground" : "font-medium text-foreground/85"}`}>
        {value}
      </div>
    </div>
  );
}
